// React Query hooks for the receivables (piutang) API.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listOutstandingByCustomer,
  listPaymentsByTransaction,
  listPaymentsByCustomer,
  listAllOutstanding,
  recordPayment,
  type RecordPaymentInput,
} from '@/lib/api/receivables';

const KEYS = {
  all: ['receivables'] as const,
  byCustomer: (customerId: string) =>
    [...KEYS.all, 'outstanding', 'customer', customerId] as const,
  byTransaction: (transactionId: string) =>
    [...KEYS.all, 'payments', 'transaction', transactionId] as const,
  paymentsByCustomer: (customerId: string) =>
    [...KEYS.all, 'payments', 'customer', customerId] as const,
  global: () => [...KEYS.all, 'outstanding', 'global'] as const,
};

export const receivablesKeys = KEYS;

// ==== Queries ====

export const useOutstandingByCustomer = (customerId: string | null) =>
  useQuery({
    queryKey: KEYS.byCustomer(customerId ?? ''),
    queryFn: () =>
      customerId
        ? listOutstandingByCustomer(customerId)
        : Promise.resolve([]),
    enabled: !!customerId,
  });

export const usePaymentsByTransaction = (transactionId: string | null) =>
  useQuery({
    queryKey: KEYS.byTransaction(transactionId ?? ''),
    queryFn: () =>
      transactionId
        ? listPaymentsByTransaction(transactionId)
        : Promise.resolve([]),
    enabled: !!transactionId,
  });

export const usePaymentsByCustomer = (customerId: string | null) =>
  useQuery({
    queryKey: KEYS.paymentsByCustomer(customerId ?? ''),
    queryFn: () =>
      customerId
        ? listPaymentsByCustomer(customerId)
        : Promise.resolve([]),
    enabled: !!customerId,
  });

export const useAllOutstanding = () =>
  useQuery({
    queryKey: KEYS.global(),
    queryFn: () => listAllOutstanding(200),
    staleTime: 30_000, // 30s — refreshes on focus anyway
  });

// ==== Mutations ====

/**
 * Record an incoming payment against a piutang transaction. Invalidates
 * every cache that might be affected: the parent transaction, the
 * customer's outstanding list, the customer's payment history, and the
 * global dashboard rollup.
 */
export const useRecordPayment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RecordPaymentInput) => recordPayment(input),
    onSuccess: (_data, input) => {
      // Parent transaction detail
      qc.invalidateQueries({
        queryKey: ['transactions', 'detail', input.transactionId],
      });
      // The transaction list of the event this txn belongs to (we
      // don't know the eventId here, so we blow away all transactions
      // list caches — fine for an MVP).
      qc.invalidateQueries({ queryKey: ['transactions', 'list'] });
      qc.invalidateQueries({ queryKey: ['transactions', 'recent'] });
      qc.invalidateQueries({ queryKey: ['transactions', 'by-customer'] });
      // Receivables caches
      qc.invalidateQueries({
        queryKey: KEYS.byTransaction(input.transactionId),
      });
      qc.invalidateQueries({ queryKey: KEYS.global() });
      // Customer-scoped caches — best effort: we don't know the
      // customerId on this mutation callback reliably (only inside
      // the closure), so we wipe the byCustomer and paymentsByCustomer
      // buckets entirely. The next render will refetch only the
      // current customer's slices.
      qc.invalidateQueries({
        queryKey: [...KEYS.all, 'outstanding', 'customer'],
      });
      qc.invalidateQueries({
        queryKey: [...KEYS.all, 'payments', 'customer'],
      });
    },
  });
};
